// Coordinate formatting helpers — single source of truth for the app.
//
// Forest Service / wildland-fire aviation standard is degrees-decimal-
// minutes (DMM). Most fire dispatchers, helicopter coordinators, and
// USFS field offices speak DMM exclusively when relaying coordinates
// over the radio. ForeFlight defaults to DMM for the same reason.
//
// We support three formats:
//   * DMM — 'N41°47.117'  W122°28.090''  (Forest Service standard)
//   * DMS — 'N41°47'07.0" W122°28'05.4"' (older sectional / Chart Supplement)
//   * DD  — '41.78528, -122.46817'         (raw machine format, GPS quick-glance)
//
// Default surface format = DMM. Detail panels that show all three
// (NavaidDetail) order them DMM > DMS > DD so the most-used format is
// at the top.
//
// Wiley feedback (2026-05-09): "that long format should be done in
// degrees minutes decimal minutes or degrees seconds as this is the
// standard for the Forrest service."

/**
 * Default user-facing format for a single coordinate pair. Always DMM
 * unless the caller explicitly wants something else.
 */
export function formatLatLon(lat: number, lon: number): string {
  return formatLatLonDMM(lat, lon);
}

export function formatLatLonDMM(lat: number, lon: number): string {
  return `${dmmPart(lat, true)}  ${dmmPart(lon, false)}`;
}

export function formatLatLonDMS(lat: number, lon: number): string {
  return `${dmsPart(lat, true)}  ${dmsPart(lon, false)}`;
}

export function formatLatLonDD(lat: number, lon: number): string {
  return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
}

function dmmPart(v: number, isLat: boolean): string {
  const hemi = v < 0 ? (isLat ? 'S' : 'W') : (isLat ? 'N' : 'E');
  const abs = Math.abs(v);
  const deg = Math.floor(abs);
  const min = (abs - deg) * 60;
  // Pad degrees to 2 (lat) / 3 (lon) so columns align in monospace
  // tables. Decimal-minutes to 3 places ≈ 1.85m at 60° lat — plenty
  // for VFR nav and a touch better than ForeFlight's default 2 places.
  return `${hemi}${deg.toString().padStart(isLat ? 2 : 3, '0')}\u00b0${min.toFixed(3)}'`;
}

function dmsPart(v: number, isLat: boolean): string {
  const hemi = v < 0 ? (isLat ? 'S' : 'W') : (isLat ? 'N' : 'E');
  const abs = Math.abs(v);
  const deg = Math.floor(abs);
  const min = Math.floor((abs - deg) * 60);
  const sec = (abs - deg - min / 60) * 3600;
  return `${hemi}${deg.toString().padStart(isLat ? 2 : 3, '0')}\u00b0${min.toString().padStart(2, '0')}'${sec.toFixed(1)}"`;
}

// ─── Parser ─────────────────────────────────────────────────────────────
//
// Accepts a wide range of human-typed lat/lon formats and returns
// `{ lat, lon }` (decimal degrees, signed) or null if it can't parse.
//
// Examples that work:
//   "41.7883, -122.4681"                    (DD signed)
//   "N41 47.30 W122 28.08"                  (DMM)
//   "N41°47.30' W122°28.08'"                (DMM with symbols)
//   "N41 47 18  W122 28 05"                 (DMS)
//   "N41°47'18\" W122°28'05\""              (DMS with symbols)
//   "41 47.30 N, 122 28.08 W"               (DMM trailing hemisphere)
//   "41.7883 / -122.4681"                   (DD with slash)

export interface ParsedLatLon { lat: number; lon: number; }

export function parseLatLon(input: string): ParsedLatLon | null {
  if (!input) return null;
  const s = input.trim();
  if (!s) return null;

  // First: try the simple DD format — two signed decimals separated by
  // a comma, slash, or whitespace.
  const dd = parseDecimalDegrees(s);
  if (dd && isValid(dd.lat, dd.lon)) return dd;

  // Otherwise: split into "lat-half" + "lon-half" and parse each.
  // We split on the first occurrence of a longitude hemisphere letter
  // (E/W), OR on a comma if both halves carry their own hemisphere.
  const halves = splitHemispheres(s);
  if (!halves) return null;
  const lat = parseHalf(halves.latStr, 'lat');
  const lon = parseHalf(halves.lonStr, 'lon');
  if (lat == null || lon == null) return null;
  if (!isValid(lat, lon)) return null;
  return { lat, lon };
}

function isValid(lat: number, lon: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lon)
    && Math.abs(lat) <= 90 && Math.abs(lon) <= 180;
}

function parseDecimalDegrees(s: string): ParsedLatLon | null {
  const m = s.match(/^\s*(-?\d+(?:\.\d+)?)[\s,/]+(-?\d+(?:\.\d+)?)\s*$/);
  if (!m) return null;
  return { lat: Number(m[1]), lon: Number(m[2]) };
}

function splitHemispheres(s: string): { latStr: string; lonStr: string } | null {
  // Strategy: find the second N/S/E/W (or first E/W, whichever comes
  // first); split there.
  const upper = s.toUpperCase();
  // Case 1: "N… E…" or "N… W…" (lat first, leading hemispheres)
  const m1 = upper.match(/^([NS][^EW]+?)([EW].+)$/);
  if (m1) return { latStr: m1[1].trim(), lonStr: m1[2].trim() };
  // Case 2: "… N, … W" (trailing hemispheres) — split on the comma.
  if (s.includes(',')) {
    const parts = s.split(',');
    if (parts.length === 2) {
      return { latStr: parts[0].trim(), lonStr: parts[1].trim() };
    }
  }
  return null;
}

function parseHalf(s: string, kind: 'lat' | 'lon'): number | null {
  if (!s) return null;
  // Pull off optional hemisphere letter (leading or trailing) and
  // remember the sign multiplier.
  let sign = 1;
  let body = s;
  // ⚠️ Hemisphere letter sits right next to a digit (e.g. 'N41 47.30')
  // so we CANNOT use \b — there's no word boundary between two word
  // characters. Match the single hemisphere char and consume it.
  const upperBody = body.toUpperCase();
  const hMatch = upperBody.match(/^([NSEW])/);
  if (hMatch) {
    const h = hMatch[1];
    if (h === 'S' || h === 'W') sign = -1;
    body = body.slice(1).trim();
  } else {
    const tMatch = upperBody.match(/([NSEW])\s*$/);
    if (tMatch) {
      const h = tMatch[1];
      if (h === 'S' || h === 'W') sign = -1;
      body = body.slice(0, tMatch.index!).trim();
    }
  }
  // Strip symbols, leaving spaces between number groups.
  const cleaned = body.replace(/[°'"′″]/g, ' ').replace(/\s+/g, ' ').trim();
  const nums = cleaned.split(/\s+/).map((p) => Number(p));
  if (nums.some((n) => !Number.isFinite(n))) return null;
  let dd: number;
  if (nums.length === 1) {
    dd = nums[0];
  } else if (nums.length === 2) {
    // DMM: degrees + decimal minutes
    dd = Math.abs(nums[0]) + Math.abs(nums[1]) / 60;
  } else if (nums.length === 3) {
    // DMS: degrees + minutes + seconds
    dd = Math.abs(nums[0]) + Math.abs(nums[1]) / 60 + Math.abs(nums[2]) / 3600;
  } else {
    return null;
  }
  // Preserve the sign of the original decimal-only form when no
  // hemisphere letter was given (e.g. "-122.5").
  const noHemi = sign === 1 && nums[0] < 0;
  const out = (noHemi ? -1 : 1) * sign * dd;
  // Range guard
  if (kind === 'lat' && Math.abs(out) > 90) return null;
  if (kind === 'lon' && Math.abs(out) > 180) return null;
  return out;
}
